import React from 'react';
import { useSelector } from 'react-redux';

import { Row, Col } from '@zillow/zrs-comet';
import { Spacer } from '@zillow/constellation';

import { DetailedDescription } from 'app/shared/components/postingPath/DetailedDescription';
import AmenitiesAndRules from 'app/shared/components/postingPath/AmenitiesAndRules';
import ContactInformation from 'app/shared/components/postingPath/ContactInformation';
import EnableApplicationsRadio from 'app/shared/components/postingPath/EnableApplicationsRadio';
import HousingConnector from 'app/shared/components/postingPath/HousingConnector';
import LeadGuaranteeBanner from 'app/shared/components/postingPath/LeadGuaranteeBanner';
import ListingDetails from 'app/shared/components/postingPath/ListingDetails';
import Media from 'app/shared/components/postingPath/Media';
import PaidInclusionOverview from 'app/shared/components/paidInclusion/ListingOverview';
import Scheduler from 'app/shared/components/postingPath/Scheduler';
import { UserSelectors, PropertiesSelectors } from 'app/shared/selectors';
import ListingActivationTerms from 'app/shared/components/ListingActivationTerms';
import RenewListing from './components/renewListing';

const SingleFamilyForm = ({
    canUpdateApplications,
    contactNameRef,
    contactPhoneRef,
    descriptionRef,
    errorObj,
    floorplansRef,
    formData,
    forRentByRef,
    immediateUpdateAndSave,
    isApplicationsAcceptedRef,
    isInputFormDisabled,
    isListingPhotoProcessing,
    isPaymentValid,
    leaseDurationRef,
    rentPriceRef,
    saveFloorplanData,
    savePropertyData,
    setFailedRenewalData,
    setSuccessfulRenewalData,
    statusMessage,
    updateErrorObj,
    updateFormData,
    updateListingPhotoProcessingStatus,
    updateOnFocus,
    updateStatusMessage,
}) => {
    const isSeattleHousingConnector = useSelector(state => UserSelectors.makeIsSeattleHousingConnector(state));
    const { leadGuarantee } = useSelector(state => PropertiesSelectors.makeCurrentProperty(state));

    const isPaymentRequired = Boolean(formData.paidInclusion && formData.paidInclusion.isPaymentRequired);
    const {
        isEligible: isEligibleForLeadGuarantee,
    } = leadGuarantee;
    const {
        seattleHousingConnectorStatus,
        isApplicationsAccepted,
        paidInclusion = {},
    } = formData;

    const { isEnabled: isListingInNucleusMarket } = paidInclusion;
    const logicForNucleusMarket =
        formData.active === true &&
        isListingInNucleusMarket === true &&
        isPaymentValid === false &&
        isPaymentRequired === false;

    const logicForNonNucleusMarket =
        formData.active === true &&
        isListingInNucleusMarket === false &&
        isPaymentValid === false &&
        isPaymentRequired === false;

    const shouldDisplayRenewListing = (logicForNucleusMarket || logicForNonNucleusMarket)
        && !formData.restrictions.isUpdateListingRestricted;

    // lead guarantee
    const renderLeadGuaranteeBanner = () => {
        const heading = 'Backed by our Lead Guarantee';
        const body = 'If you don\'t receive a lead within a week, it\'s free to list until you do (for up to 8 weeks).';
        const actionLabel = 'Explore Lead Guarantee';
        const actionPath = 'https://help.zillowrentalmanager.com/hc/en-us/articles/360048462014';

        return (
            <LeadGuaranteeBanner
                heading={heading}
                body={body}
                actionLabel={actionLabel}
                actionPath={actionPath}
                actionTarget="_blank"
            />
        );
    };

    return (
        <Row>
            <Col xs={0} lg={2} />
            <Col xs={12} lg={8}>
                {shouldDisplayRenewListing && (
                    <RenewListing
                        formData={formData}
                        updateStatusMessage={updateStatusMessage}
                        setSuccessfulRenewalData={setSuccessfulRenewalData}
                        setFailedRenewalData={setFailedRenewalData}
                    />
                )}
                {!formData.isFeed && formData.active && (
                    <Spacer marginBottom="md">
                        <PaidInclusionOverview />
                    </Spacer>
                )}
                {(!formData.active && !formData.archived && isEligibleForLeadGuarantee) && (
                    <Spacer marginBottom="md">
                        {renderLeadGuaranteeBanner()}
                    </Spacer>
                )}
                <ListingDetails
                    descriptionRef={descriptionRef}
                    errorObj={errorObj}
                    floorplansRef={floorplansRef}
                    formData={formData}
                    immediateUpdateAndSave={immediateUpdateAndSave}
                    isInputFormDisabled={isInputFormDisabled}
                    leaseDurationRef={leaseDurationRef}
                    rentPriceRef={rentPriceRef}
                    saveFloorplanData={saveFloorplanData}
                    savePropertyData={savePropertyData}
                    updateErrorObj={updateErrorObj}
                    updateFormData={updateFormData}
                    updateOnFocus={updateOnFocus}
                />
                <AmenitiesAndRules
                    formData={formData}
                    errorObj={errorObj}
                    updateErrorObj={updateErrorObj}
                    immediateUpdateAndSave={immediateUpdateAndSave}
                    updateFormData={updateFormData}
                    updateStatusMessage={updateStatusMessage}
                    isInputFormDisabled={isInputFormDisabled}
                />
                <DetailedDescription
                    descriptionRef={descriptionRef}
                    errorObj={errorObj}
                    formData={formData}
                    isInputFormDisabled={isInputFormDisabled}
                    savePropertyData={savePropertyData}
                    updateFormData={updateFormData}
                    updateOnFocus={updateOnFocus}
                />
                <Media
                    errorObj={errorObj}
                    formData={formData}
                    hasError={Boolean(errorObj.photos)}
                    isInputFormDisabled={isInputFormDisabled}
                    isListingPhotoProcessing={isListingPhotoProcessing}
                    statusMessage={statusMessage}
                    tours={[formData.tours[0]]}
                    tourType="singleFamily"
                    updateErrorObj={updateErrorObj}
                    updateFormData={updateFormData}
                    updateListingPhotoProcessingStatus={updateListingPhotoProcessingStatus}
                    updateStatusMessage={updateStatusMessage}
                />
                <Spacer marginY="md" />
                <ContactInformation
                    contactNameRef={contactNameRef}
                    contactPhoneRef={contactPhoneRef}
                    errorObj={errorObj}
                    formData={formData}
                    forRentByRef={forRentByRef}
                    immediateUpdateAndSave={immediateUpdateAndSave}
                    isInputFormDisabled={isInputFormDisabled}
                    isPaymentRequired={isPaymentRequired}
                    savePropertyData={savePropertyData}
                    updateErrorObj={updateErrorObj}
                    updateFormData={updateFormData}
                    updateOnFocus={updateOnFocus}
                    updateStatusMessage={updateStatusMessage}
                />
                <Scheduler
                    isInputFormDisabled={isInputFormDisabled}
                    schedule={formData.schedule}
                    updateStatusMessage={updateStatusMessage}
                    updateFormData={updateFormData}
                    listingAlias={formData.listingAlias}
                />
                {isSeattleHousingConnector && (
                    <HousingConnector
                        initSelectedOption={seattleHousingConnectorStatus}
                        handleSelectChange={immediateUpdateAndSave}
                    />
                )}
                {canUpdateApplications && (
                    <EnableApplicationsRadio
                        isApplicationsAcceptedRef={isApplicationsAcceptedRef}
                        errorObj={errorObj}
                        updateErrorObj={updateErrorObj}
                        isApplicationsAccepted={isApplicationsAccepted}
                        handleSelectionChange={immediateUpdateAndSave}
                    />
                )}
                <ListingActivationTerms
                    textCenter={true}
                    actionText={`By clicking ${formData.active ? 'Deactivate' : 'Publish'} listing`}
                />
            </Col>
            <Col xs={0} lg={2} />
        </Row>
    );
};

export default SingleFamilyForm;
